import { client } from "@lib/api"
import * as Sentry from "@sentry/react"
import { useCallback, useEffect, useState } from "react"

export type AddAddressParams = {
    name: string
    address: string
}

export type EditAddressParams = AddAddressParams & {
    uuid: string
}

export type RemoveAddressParams = {
    uuid: string
}

export type Address = {
    address_uuid: string
    name: string
    address: string
    address_tag?: string
    network: string
    asset: string
}

const useAddressBookManagement = (fetchAddresses: boolean = true) => {
    const addAddress = async ({ 
        name,
        address
    }: AddAddressParams) => {
        try {
            return await client.doPost('/wallet/address', {
                name,
                address
            })
        } catch (error) {
            Sentry.captureException(error)
            throw error
        }
    }

    const editAddress = async (params: EditAddressParams) => {
        try {
            return await client.doPut(`/wallet/address/${params.uuid}`, {
                name: params.name,
                address: params.address,
            })
        } catch (error) {
            Sentry.captureException(error)
            throw error
        }
    }

    const removeAddress = async (params: RemoveAddressParams) => {
        try {
            return await client.doDelete(`/wallet/address/${params.uuid}`)
        } catch (error) {
            Sentry.captureException(error)
            throw error
        }
    }

    const [addresses, setAddresses] = useState<Address[]>([])
    const [localLoading, setLocalLoading] = useState(true);

    const getDataBase = async () => {
        try {
            setLocalLoading(true)
            const addresses = await client.doGet('/wallet/address')
            setAddresses(addresses)
        } catch (error) {
            Sentry.captureException(error)
            throw error
        } finally {
            setLocalLoading(false)
        }
    }

    const getData = useCallback(() => {
        getDataBase()
    }, [])

    useEffect(() => {
        if (!fetchAddresses) return
        getData()
    }, [fetchAddresses])

    return {
        addAddress,
        editAddress,
        removeAddress,
        addresses: addresses as Address[],
        isLoading: localLoading,
        reload: getDataBase,
    }
}

export default useAddressBookManagement
